/**
 * Unified API Client - Centralized API utility with base URL, auth headers, and unified error handling
 * Replaces all direct API calls and provides consistent error messages to users while logging technical details.
 */

import { useCallback, useMemo } from 'react';
import { authService } from '@/lib/auth-direct';
import { logger } from '@/lib/logger';
import { errorHandler } from '@/lib/error-handler';
import { buildApiUrl } from '@/utils/api-base-url';

// Enhanced error types for better error handling
export interface ApiErrorDetails {
  message: string;
  userMessage: string;
  /** i18n key for the user-facing message so callers can display a translated string */
  userMessageKey?: string;
  /**
   * A human-readable reason supplied by the server (e.g. the 429 ProblemDetails
   * detail). When present it should be shown verbatim, ahead of the generic
   * translation for userMessageKey.
   */
  serverDetail?: string;
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
  timestamp: string;
  requestId?: string;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  error?: ApiErrorDetails;
  success: boolean;
  status: number;
  headers?: Record<string, string>;
}

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  signal?: AbortSignal;
  skipAuth?: boolean;
  enableGlobalErrorHandling?: boolean;
  logLevel?: 'debug' | 'info' | 'warn' | 'error' | 'none';
  /**
   * 'blob' forces the success body to be read as a Blob regardless of its
   * content type (used by file downloads, where even JSON artifacts must
   * stay binary). Defaults to content-type driven parsing.
   */
  responseType?: 'auto' | 'blob';
}

/**
 * Blob object URLs must not be revoked in the same tick as the anchor click
 * or `window.open` that consumes them: slow devices may not have started
 * reading the blob yet, which aborts the download/preview. Revocation is
 * deferred by this delay instead. (See audit issue 81.)
 */
const OBJECT_URL_REVOKE_DELAY_MS = 60_000;

/**
 * Triggers a browser "save file" for an already-fetched Blob via a temporary
 * object URL. The object URL is revoked on a delay, never in the same tick.
 */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  if (typeof window === 'undefined') return;
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(
    () => window.URL.revokeObjectURL(url),
    OBJECT_URL_REVOKE_DELAY_MS
  );
}

/**
 * Opens an already-fetched Blob in a new browser tab (e.g. PDF preview) via a
 * temporary object URL, revoked on the same deferred schedule as saves.
 */
export function openBlobInNewTab(blob: Blob): void {
  if (typeof window === 'undefined') return;
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(
    () => window.URL.revokeObjectURL(url),
    OBJECT_URL_REVOKE_DELAY_MS
  );
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  bytesPerSecond?: number;
  estimatedTimeRemaining?: number;
}

export interface UploadConfig extends RequestConfig {
  onProgress?: (progress: UploadProgress) => void;
  fieldName?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Enhanced API Error class with user-friendly messages
 */
export class UnifiedApiError extends Error {
  constructor(
    public details: ApiErrorDetails,
    public originalError?: Error | unknown
  ) {
    super(details.userMessage);
    this.name = 'UnifiedApiError';
  }
}

/**
 * Extracts a string-typed field from an unknown value, safely.
 */
function stringField(obj: unknown, field: string): string | undefined {
  if (typeof obj !== 'object' || obj === null) return undefined;
  const value = (obj as Record<string, unknown>)[field];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Main Unified API Client class.
 *
 * Contract: every request method (`get`, `post`, `put`, `patch`, `delete`,
 * `uploadFile`) never rejects — it always resolves to an `ApiResponse`
 * envelope (`success: true` with optional `data`, or `success: false` with
 * `error` details). A 204/empty body is a success with `data === undefined`.
 * The single exception is `downloadFile`, which returns a `Blob` and throws
 * `UnifiedApiError` on failure.
 *
 * Retry policy: only transient failures are retried — HTTP 408,
 * 502/503/504, and network errors. 429 is NOT retried: the backend uses it
 * for the concurrent-matching-jobs limit, which does not clear in seconds. Other statuses (400, 404, 409, 500, …)
 * fail immediately. A 401 gets exactly one refresh-and-replay attempt; if it
 * still fails, the session is cleared via `authService.notifySessionExpired()`
 * and the auth context redirects to sign-in.
 */
export class UnifiedApiClient {
  private baseUrl: string;
  private defaultTimeout = 30000;
  private defaultRetries = 2;
  private inflightRequests = new Map<string, Promise<ApiResponse<unknown>>>();
  private activeControllers = new Set<AbortController>();
  private activeUploads = new Set<XMLHttpRequest>();

  private static readonly RETRYABLE_STATUSES = new Set([408, 502, 503, 504]);

  constructor() {
    // Use the centralized API URL helper
    this.baseUrl = buildApiUrl('').replace(/\/$/, '');

    // In test environment, we might need to adjust the base URL
    if (typeof window === 'undefined' && process.env.NODE_ENV === 'test') {
      this.baseUrl = 'http://localhost:5000/api';
    }
  }

  private generateRequestId(endpoint: string, method: string): string {
    return `${method.toUpperCase()}-${endpoint}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  /**
   * Reads the double-submit CSRF token issued by the backend. The cookie is
   * host-scoped in dev (localhost, ports are irrelevant to cookies) and
   * parent-domain-scoped in production (Csrf__CookieDomain), so the app origin
   * can read it. Returns null before the first API response has set it.
   */
  private getCsrfToken(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private csrfFetchInFlight: Promise<void> | null = null;

  /**
   * Ensures a CSRF cookie exists before a mutating request; the dedicated
   * anonymous endpoint triggers the middleware to issue one. Concurrent first
   * mutations share one in-flight fetch — parallel fetches would each rotate
   * the token+session cookie pair and race a header token against a newer
   * cookie, failing validation.
   */
  private async ensureCsrfToken(): Promise<string | null> {
    const existing = this.getCsrfToken();
    if (existing) return existing;
    this.csrfFetchInFlight ??= fetch(`${this.baseUrl}/security/csrf-token`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(() => undefined)
      .catch(() => {
        // The mutating request will surface the real error.
      })
      .finally(() => {
        this.csrfFetchInFlight = null;
      });
    await this.csrfFetchInFlight;
    return this.getCsrfToken();
  }

  private static isMutatingMethod(method: string): boolean {
    const upper = method.toUpperCase();
    return upper === 'POST' || upper === 'PUT' || upper === 'DELETE' || upper === 'PATCH';
  }

  private isRetryableStatus(status: number): boolean {
    return UnifiedApiClient.RETRYABLE_STATUSES.has(status);
  }

  /**
   * Dedup key for concurrent identical GETs. Includes the request-shaping
   * parts of the config so GETs to the same endpoint with different headers
   * or auth settings are not collapsed into one request.
   */
  private buildDedupKey(endpoint: string, config: RequestConfig): string {
    const {
      headers = {},
      skipAuth = false,
      timeout,
      retries,
      responseType = 'auto',
    } = config;
    return `GET:${endpoint}:${skipAuth}:${timeout ?? ''}:${retries ?? ''}:${responseType}:${JSON.stringify(headers)}`;
  }

  private createErrorDetails(
    error: unknown,
    status?: number,
    endpoint?: string,
    requestId?: string
  ): ApiErrorDetails {
    const timestamp = new Date().toISOString();
    const isNetworkError =
      error instanceof TypeError && error.message.includes('fetch');
    const errorMessage = stringField(error, 'message');
    const errorName = stringField(error, 'name');

    // Start with user-friendly message
    let userMessage = 'An unexpected error occurred. Please try again.';
    let userMessageKey = 'api.errors.unknownError.message';
    let code = 'UNKNOWN_ERROR';
    let serverDetail: string | undefined;

    // Handle specific HTTP status codes
    if (status) {
      switch (status) {
        case 400:
          userMessage =
            'Invalid request. Please check your input and try again.';
          userMessageKey = 'api.errors.badRequest.message';
          code = 'BAD_REQUEST';
          break;
        case 401:
          userMessage = 'Your session has expired. Please sign in again.';
          userMessageKey = 'api.errors.unauthorized.message';
          code = 'UNAUTHORIZED';
          break;
        case 403:
          userMessage = "You don't have permission to perform this action.";
          userMessageKey = 'api.errors.forbidden.message';
          code = 'FORBIDDEN';
          break;
        case 404:
          userMessage = 'The requested information was not found.';
          userMessageKey = 'api.errors.notFound.message';
          code = 'NOT_FOUND';
          break;
        case 408:
          userMessage =
            'Request timed out. Please check your connection and try again.';
          userMessageKey = 'api.errors.requestCancelled.message';
          code = 'TIMEOUT';
          break;
        case 409:
          userMessage = 'There was a conflict. Please refresh and try again.';
          userMessageKey = 'api.errors.clientError.message';
          code = 'CONFLICT';
          break;
        case 422:
          userMessage = 'The provided information is not valid.';
          userMessageKey = 'api.errors.validation.message';
          code = 'VALIDATION_ERROR';
          break;
        case 429: {
          // The backend's 429 ProblemDetails carries the actual reason (e.g.
          // the concurrent-matching-jobs limit); prefer it over the generic text.
          serverDetail = stringField(error, 'detail') || undefined;
          userMessage =
            serverDetail ||
            'Too many requests. Please wait a moment and try again.';
          userMessageKey = 'api.errors.rateLimited.message';
          code = 'RATE_LIMITED';
          break;
        }
        case 500:
          userMessage =
            'Server error. Our team has been notified and is working on it.';
          userMessageKey = 'api.errors.serverError.message';
          code = 'SERVER_ERROR';
          break;
        case 502:
        case 503:
        case 504:
          userMessage =
            'Service temporarily unavailable. Please try again in a few moments.';
          userMessageKey = 'api.errors.serviceUnavailable.message';
          code = 'SERVICE_UNAVAILABLE';
          break;
        default:
          userMessage = `Request failed (${status}). Please try again.`;
          userMessageKey = 'api.errors.unknownError.message';
          code = 'HTTP_ERROR';
      }
    }

    // Handle network errors (fetch TypeError or XHR network failures)
    if (
      isNetworkError ||
      (!status && errorMessage?.toLowerCase().includes('network'))
    ) {
      userMessage =
        'Network error. Please check your internet connection and try again.';
      userMessageKey = 'api.errors.networkError.message';
      code = 'NETWORK_ERROR';
    }

    // Handle timeout errors
    if (errorName === 'AbortError' || errorMessage?.includes('timeout')) {
      userMessage = 'Request timed out. Please try again.';
      userMessageKey = 'api.errors.requestCancelled.message';
      code = 'TIMEOUT';
    }

    const responseMessage =
      errorMessage || stringField(error, 'error') || stringField(error, 'detail');
    if (responseMessage && typeof responseMessage === 'string') {
      // Log technical details but keep user message friendly
      logger.warn('[UnifiedApiClient] API Error Response', {
        requestId,
        endpoint,
        status,
        responseMessage,
        code,
      });
    }

    return {
      message: responseMessage || 'Unknown error occurred',
      userMessage,
      userMessageKey,
      serverDetail,
      status,
      code,
      details:
        typeof error === 'object' && error !== null && 'details' in error
          ? (error as Record<string, unknown>).details as Record<string, unknown>
          : {},
      timestamp,
      requestId,
    };
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const tokens = await authService.ensureValidToken();
      if (tokens?.accessToken) {
        return {
          Authorization: `Bearer ${tokens.accessToken}`,
        };
      }
    } catch (error) {
      logger.warn('[UnifiedApiClient] Failed to get auth token', { error });
    }

    return {};
  }

  /**
   * Implements exponential backoff with jitter for retry logic
   */
  private calculateDelay(attempt: number, baseDelay = 1000): number {
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return exponentialDelay + jitter;
  }

  private serializeBody(
    body: unknown,
    contentType: string
  ): string | FormData | undefined {
    if (!body) return undefined;

    if (contentType.includes('application/json')) {
      return JSON.stringify(body);
    }

    if (contentType.includes('application/x-www-form-urlencoded')) {
      if (typeof body === 'string') {
        return body;
      }
      if (typeof body === 'object') {
        return new URLSearchParams(body as Record<string, string>).toString();
      }
    }

    return String(body);
  }

  private logRequest(
    level: 'debug' | 'info' | 'warn' | 'error' | 'none',
    message: string,
    data: Record<string, unknown>
  ): void {
    if (level === 'none') return;

    const logData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    switch (level) {
      case 'debug':
        logger.debug(`[UnifiedApiClient] ${message}`, logData);
        break;
      case 'info':
        logger.info(`[UnifiedApiClient] ${message}`, logData);
        break;
      case 'warn':
        logger.warn(`[UnifiedApiClient] ${message}`, logData);
        break;
      case 'error':
        logger.error(`[UnifiedApiClient] ${message}`, logData);
        break;
    }
  }

  /**
   * Core HTTP request method with retry logic and comprehensive error handling
   */
  private async makeRequest<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const { method = 'GET' } = config;

    // Dedup concurrent identical GETs. Requests carrying an external abort
    // signal are excluded: sharing one promise would let a single caller's
    // abort cancel the request for everyone else.
    if (method.toUpperCase() === 'GET' && !config.signal) {
      const dedupKey = this.buildDedupKey(endpoint, config);
      const inflight = this.inflightRequests.get(dedupKey);
      if (inflight) {
        return inflight as Promise<ApiResponse<T>>;
      }
      const promise = this._executeRequest<T>(endpoint, config);
      this.inflightRequests.set(dedupKey, promise as Promise<ApiResponse<unknown>>);
      promise.finally(() => this.inflightRequests.delete(dedupKey));
      return promise;
    }

    return this._executeRequest<T>(endpoint, config);
  }

  private async _executeRequest<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      body,
      headers = {},
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      retryDelay = 1000,
      signal,
      skipAuth = false,
      enableGlobalErrorHandling = true,
      logLevel = 'info',
    } = config;
    const requestId = this.generateRequestId(endpoint, method);
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

    this.logRequest(logLevel, `Starting ${method} request`, {
      requestId,
      endpoint,
      method,
      url,
      hasBody: !!body,
      timeout,
      retries,
    });

    let lastError: Error | null = null;
    let attemptedUnauthorizedRefresh = false;
    let attemptsMade = 0;

    for (let attempt = 0; attempt <= retries; attempt++) {
      attemptsMade++;
      try {
        // Check for external abort signal
        if (signal?.aborted) {
          throw new DOMException('Request was aborted by the caller', 'AbortError');
        }

        // Get authentication headers if needed
        const authHeaders = skipAuth ? {} : await this.getAuthHeaders();

        // Set up default headers
        const defaultHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Request-ID': requestId,
          ...authHeaders,
          ...headers,
        };

        // Backend CSRF middleware rejects mutating requests without the
        // double-submit header (X-CSRF-TOKEN matching the XSRF-TOKEN cookie).
        if (UnifiedApiClient.isMutatingMethod(method)) {
          const csrfToken = await this.ensureCsrfToken();
          if (csrfToken) {
            defaultHeaders['X-CSRF-TOKEN'] = csrfToken;
          }
        }

        // Create abort controller for timeout
        const controller = new AbortController();
        this.activeControllers.add(controller);
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        // Combine external signal with our timeout signal; the listener is
        // removed after the attempt settles so retries don't accumulate
        // listeners on the caller's signal.
        const onExternalAbort = () => controller.abort();
        signal?.addEventListener('abort', onExternalAbort, { once: true });

        // Serialize body if present
        const serializedBody = this.serializeBody(
          body,
          defaultHeaders['Content-Type'] || 'application/json'
        );

        this.logRequest(
          logLevel,
          `Sending request (attempt ${attempt + 1}/${retries + 1})`,
          {
            requestId,
            headers: defaultHeaders,
            hasBody: !!serializedBody,
          }
        );

        let response: Response;
        try {
          response = await fetch(url, {
            method,
            headers: defaultHeaders,
            body: serializedBody,
            signal: controller.signal,
            // Cookies (CSRF pair) must flow to the API origin, which is a
            // sibling subdomain in production.
            credentials: 'include',
          });
        } finally {
          clearTimeout(timeoutId);
          signal?.removeEventListener('abort', onExternalAbort);
          this.activeControllers.delete(controller);
        }

        // Log response details
        this.logRequest(logLevel, 'Received response', {
          requestId,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
        });

        // Handle successful responses
        if (response.ok) {
          let data: T | undefined;

          try {
            // Handle empty responses (e.g., 204 No Content)
            const contentType = response.headers.get('content-type');
            if (response.status === 204 || response.status === 205) {
              data = undefined;
            } else if (config.responseType === 'blob') {
              data = (await response.blob()) as unknown as T;
            } else if (contentType && contentType.includes('application/json')) {
              const text = await response.text();
              if (text) {
                data = JSON.parse(text) as T;
              }
            } else if (contentType && contentType.includes('text/')) {
              const text = await response.text();
              data = text as unknown as T;
            } else {
              // Handle binary data or other content types
              data = (await response.blob()) as unknown as T;
            }
          } catch (parseError) {
            this.logRequest('warn', 'Failed to parse response body', {
              requestId,
              status: response.status,
              parseError,
            });
          }

          return {
            data,
            success: true,
            status: response.status,
            headers: Object.fromEntries(response.headers.entries()),
          };
        }

        // Handle error responses
        let errorData: unknown = null;
        try {
          const errorText = await response.text();
          if (errorText) {
            errorData = JSON.parse(errorText);
          }
        } catch {
          // Ignore parsing errors for error responses
        }

        const errorDetails = this.createErrorDetails(
          errorData,
          response.status,
          endpoint,
          requestId
        );
        lastError = new UnifiedApiError(errorDetails);

        // 401: exactly one refresh-and-replay attempt; if the session cannot
        // be refreshed (or the replay is 401 again), expire the session so
        // the auth context clears state and redirects to sign-in.
        if (response.status === 401 && !skipAuth) {
          if (
            !attemptedUnauthorizedRefresh &&
            (await authService.refreshSessionAfterUnauthorized())
          ) {
            attemptedUnauthorizedRefresh = true;
            attempt--; // The replay does not consume a retry attempt
            continue;
          }
          authService.notifySessionExpired();
          break;
        }

        // Only transient statuses are retried; everything else fails now.
        if (!this.isRetryableStatus(response.status)) {
          break;
        }
      } catch (error) {
        lastError = error as Error;

        // Don't retry on abort (external cancellation or timeout)
        if (error instanceof Error && error.name === 'AbortError') {
          break;
        }

        // Network errors fall through to the retry backoff below
      }

      // Don't retry on the last attempt
      if (attempt === retries) {
        break;
      }

      // Calculate delay and wait before retrying
      const delay = this.calculateDelay(attempt, retryDelay);
      this.logRequest('info', `Retrying request after ${Math.round(delay)}ms`, {
        requestId,
        attempt: attempt + 1,
        retries: retries + 1,
        error: lastError?.message,
      });

      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // Non-retryable statuses break out after a single attempt, so report what
    // actually happened rather than the configured ceiling.
    this.logRequest('error', 'Request failed', {
      requestId,
      endpoint,
      method,
      error: lastError,
      attempts: attemptsMade,
      maxAttempts: retries + 1,
    });

    const finalError =
      lastError instanceof UnifiedApiError
        ? lastError
        : new UnifiedApiError(
            this.createErrorDetails(lastError, undefined, endpoint, requestId),
            lastError
          );

    // Trigger global error handling if enabled
    if (enableGlobalErrorHandling && errorHandler) {
      try {
        errorHandler.handleError(finalError);
      } catch (handlerError) {
        this.logRequest('error', 'Global error handler failed', {
          requestId,
          handlerError,
        });
      }
    }

    return {
      error: finalError.details,
      success: false,
      status: finalError.details.status || 0,
    };
  }

  async get<T>(
    endpoint: string,
    config: Omit<RequestConfig, 'method'> = {}
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    config: Omit<RequestConfig, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'POST', body });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    config: Omit<RequestConfig, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PUT', body });
  }

  async delete<T>(
    endpoint: string,
    config: Omit<RequestConfig, 'method'> = {}
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    config: Omit<RequestConfig, 'method' | 'body'> = {}
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    config: UploadConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      onProgress,
      fieldName = 'file',
      metadata = {},
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      retryDelay = 2000,
      skipAuth = false,
      enableGlobalErrorHandling = true,
      signal,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ..._requestConfig
    } = config;

    const requestId = this.generateRequestId(endpoint, 'UPLOAD');
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

    this.logRequest('info', 'Starting file upload', {
      requestId,
      endpoint,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fieldName,
      hasMetadata: Object.keys(metadata).length > 0,
    });

    let lastError: Error | null = null;
    let attemptedUnauthorizedRefresh = false;

    for (let attempt = 0; attempt <= retries; attempt++) {
      // Cleanup callbacks registered inside the attempt (XHR tracking,
      // external abort listener); run after the attempt settles.
      const attemptCleanups: Array<() => void> = [];

      try {
        // Check for abort signals
        if (signal?.aborted) {
          throw new DOMException('Upload was aborted by the caller', 'AbortError');
        }

        // Get authentication headers if needed
        const authHeaders = skipAuth ? {} : await this.getAuthHeaders();
        const csrfToken = await this.ensureCsrfToken();

        // Create FormData for file upload
        const formData = new FormData();
        formData.append(fieldName, file);

        // Add metadata
        Object.entries(metadata).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, String(value));
          }
        });

        // Awaited (not returned) so rejections are caught by the retry loop
        // below and uploads resolve to the same ApiResponse envelope as every
        // other request method instead of throwing.
        return await new Promise<ApiResponse<T>>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          this.activeUploads.add(xhr);
          attemptCleanups.push(() => this.activeUploads.delete(xhr));
          const startTime = Date.now();

          // Handle progress updates
          if (onProgress) {
            xhr.upload.addEventListener('progress', event => {
              if (event.lengthComputable) {
                const currentTime = Date.now();
                const elapsedTime = (currentTime - startTime) / 1000; // seconds
                const bytesPerSecond = event.loaded / elapsedTime;
                const remainingBytes = event.total - event.loaded;
                const estimatedTimeRemaining = remainingBytes / bytesPerSecond;

                const progress: UploadProgress = {
                  loaded: event.loaded,
                  total: event.total,
                  percentage: Math.round((event.loaded / event.total) * 100),
                  bytesPerSecond: Math.round(bytesPerSecond),
                  estimatedTimeRemaining: Math.round(estimatedTimeRemaining),
                };

                onProgress(progress);
              }
            });
          }

          // Handle completion
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = xhr.responseText
                  ? JSON.parse(xhr.responseText)
                  : null;
                this.logRequest('info', 'File upload completed successfully', {
                  requestId,
                  status: xhr.status,
                  responseSize: xhr.responseText.length,
                });

                resolve({
                  data,
                  success: true,
                  status: xhr.status,
                  headers: this.parseXhrHeaders(xhr.getAllResponseHeaders()),
                });
              } catch (parseError) {
                const errorDetails = this.createErrorDetails(
                  {
                    message: 'Failed to parse upload response',
                    details: parseError,
                  },
                  xhr.status,
                  endpoint,
                  requestId
                );
                reject(new UnifiedApiError(errorDetails, parseError));
              }
            } else {
              let errorData = null;
              try {
                errorData = JSON.parse(xhr.responseText);
              } catch {
                // Ignore parsing errors
              }

              const errorDetails = this.createErrorDetails(
                errorData,
                xhr.status,
                endpoint,
                requestId
              );
              reject(new UnifiedApiError(errorDetails));
            }
          });

          // Handle errors
          xhr.addEventListener('error', () => {
            const errorDetails = this.createErrorDetails(
              { message: 'Upload failed due to network error' },
              0,
              endpoint,
              requestId
            );
            reject(new UnifiedApiError(errorDetails));
          });

          xhr.addEventListener('abort', () => {
            const errorDetails = this.createErrorDetails(
              { message: 'Upload was cancelled' },
              0,
              endpoint,
              requestId
            );
            reject(new UnifiedApiError({ ...errorDetails, code: 'CANCELLED' }));
          });

          // Handle timeout
          xhr.addEventListener('timeout', () => {
            const errorDetails = this.createErrorDetails(
              { message: 'Upload timed out' },
              0,
              endpoint,
              requestId
            );
            reject(new UnifiedApiError({ ...errorDetails, code: 'TIMEOUT' }));
          });

          // Configure and send request
          xhr.timeout = timeout;
          xhr.open('POST', url);
          xhr.withCredentials = true;

          // Set headers
          Object.entries(authHeaders).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
          });

          // Add X-Request-ID header
          xhr.setRequestHeader('X-Request-ID', requestId);

          // Double-submit CSRF header (uploads are mutating requests)
          if (csrfToken) {
            xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken);
          }

          // Handle abort signal; the listener is removed after the attempt
          // settles so retries don't accumulate listeners on the signal.
          if (signal) {
            const onExternalAbort = () => xhr.abort();
            signal.addEventListener('abort', onExternalAbort, { once: true });
            attemptCleanups.push(() =>
              signal.removeEventListener('abort', onExternalAbort)
            );
          }

          xhr.send(formData);
        });
      } catch (error) {
        lastError = error as Error;

        // Don't retry on external abort
        if (error instanceof Error && error.name === 'AbortError') {
          break;
        }

        if (error instanceof UnifiedApiError) {
          const { status, code } = error.details;

          // Don't retry on cancellation or timeout
          if (code === 'CANCELLED' || code === 'TIMEOUT') {
            break;
          }

          // 401: exactly one refresh-and-replay attempt, then session expiry
          // (same policy as makeRequest).
          if (status === 401 && !skipAuth) {
            if (
              !attemptedUnauthorizedRefresh &&
              (await authService.refreshSessionAfterUnauthorized())
            ) {
              attemptedUnauthorizedRefresh = true;
              attempt--; // The replay does not consume a retry attempt
              continue;
            }
            authService.notifySessionExpired();
            break;
          }

          // Only transient statuses are retried; everything else fails now.
          if (status && !this.isRetryableStatus(status)) {
            break;
          }
        }
      } finally {
        attemptCleanups.forEach(cleanup => cleanup());
      }

      // Don't retry on the last attempt
      if (attempt === retries) {
        break;
      }

      // Wait before retrying
      const delay = this.calculateDelay(attempt, retryDelay);
      this.logRequest('info', `Retrying upload after ${Math.round(delay)}ms`, {
        requestId,
        attempt: attempt + 1,
        retries: retries + 1,
        error: lastError?.message,
      });

      await new Promise(resolve => setTimeout(resolve, delay));
    }

    // All retries failed
    const finalError =
      lastError instanceof UnifiedApiError
        ? lastError
        : new UnifiedApiError(
            this.createErrorDetails(lastError, undefined, endpoint, requestId),
            lastError
          );

    // Trigger global error handling if enabled
    if (enableGlobalErrorHandling && errorHandler) {
      try {
        errorHandler.handleError(finalError);
      } catch (handlerError) {
        this.logRequest('error', 'Global error handler failed', {
          requestId,
          handlerError,
        });
      }
    }

    return {
      error: finalError.details,
      success: false,
      status: finalError.details.status || 0,
    };
  }

  /**
   * Downloads a file through the standard request pipeline, so downloads get
   * the same auth-header handling, 401 refresh-and-replay, transient-status
   * retry policy, timeout, and abort tracking as every other request. When a
   * `filename` is given the blob is additionally saved via the browser (one
   * fetch, one save — callers must not save the returned blob again).
   */
  async downloadFile(
    endpoint: string,
    filename?: string,
    config: Omit<RequestConfig, 'method' | 'responseType'> = {}
  ): Promise<Blob> {
    const response = await this.makeRequest<Blob>(endpoint, {
      ...config,
      method: 'GET',
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream, application/pdf, image/*, text/*',
        ...(config.headers || {}),
      },
    });

    if (!response.success || !(response.data instanceof Blob)) {
      throw new UnifiedApiError(
        response.error ?? {
          message: 'Download failed: empty response',
          userMessage: 'Failed to download file. Please try again.',
          userMessageKey: 'api.errors.unknownError.message',
          status: response.status,
          code: 'DOWNLOAD_ERROR',
          timestamp: new Date().toISOString(),
        }
      );
    }

    if (filename) {
      saveBlobAsFile(response.data, filename);
    }

    return response.data;
  }

  /**
   * Fetches a file through the standard request pipeline and opens it in a
   * new browser tab (view-in-browser, e.g. PDF preview).
   */
  async openFileInNewTab(
    endpoint: string,
    config: Omit<RequestConfig, 'method' | 'responseType'> = {}
  ): Promise<void> {
    const blob = await this.downloadFile(endpoint, undefined, config);
    openBlobInNewTab(blob);
  }

  private parseXhrHeaders(headersString: string): Record<string, string> {
    const headers: Record<string, string> = {};
    if (!headersString) return headers;

    headersString.split('\r\n').forEach(line => {
      const [key, ...values] = line.split(': ');
      if (key && values.length > 0) {
        headers[key] = values.join(': ');
      }
    });

    return headers;
  }

  /**
   * Cancel all in-flight requests: aborts active fetches and XHR uploads and
   * clears the GET dedup map. Cancelled requests settle with a standard
   * error envelope.
   */
  cancelAllRequests(): void {
    this.activeControllers.forEach(controller => controller.abort());
    this.activeControllers.clear();
    this.activeUploads.forEach(xhr => xhr.abort());
    this.activeUploads.clear();
    this.inflightRequests.clear();
  }

  getStats(): { activeRequests: number } {
    return {
      activeRequests: this.activeControllers.size + this.activeUploads.size,
    };
  }

  cleanup(): void {
    this.cancelAllRequests();
  }
}

// Create and export singleton instance
export const unifiedApiClient = new UnifiedApiClient();

export default unifiedApiClient;

/**
 * An Error subclass that carries an i18n key so consumers can render a
 * translated message via `t(err.i18nKey, err.message)` instead of displaying
 * the hardcoded English fallback in `err.message`. When `serverDetail` is set,
 * show it verbatim instead — it is the server's specific reason (e.g. which
 * rate limit was hit) and beats the generic translation.
 */
export class TranslatableError extends Error {
  constructor(
    message: string,
    public readonly i18nKey: string,
    public readonly serverDetail?: string
  ) {
    super(message);
    this.name = 'TranslatableError';
  }
}

/**
 * Per-call options for `useAuthenticatedApi().upload`.
 */
export interface UploadCallOptions {
  headers?: Record<string, string>;
  fieldName?: string;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  timeout?: number;
}

/**
 * Per-call options for `useAuthenticatedApi().get`.
 */
export interface GetCallOptions {
  headers?: Record<string, string>;
  /**
   * Overrides the hook-wide setting for this one request. Pass `false` when the
   * caller handles the failure itself and a global toast would be noise — for
   * example probing an endpoint that is expected to answer 4xx.
   */
  enableGlobalErrorHandling?: boolean;
}

/**
 * Per-call options for `useAuthenticatedApi().download` / `.openInNewTab`.
 */
export interface DownloadCallOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeout?: number;
}

/**
 * The single React hook over the UnifiedApiClient singleton.
 * Provides memoized callbacks that unwrap ApiResponse and throw TranslatableError
 * on failure, so callers can use try/catch with translated error messages.
 * Covers JSON verbs, uploads (with progress), file downloads, and
 * view-in-browser previews — pages must not hand-roll fetch/blob logic.
 */
export function useAuthenticatedApi(
  enableGlobalErrorHandling: boolean = true
) {
  const unwrap = useCallback(
    <T>(response: ApiResponse<T>): T => {
      // Success is decided by the envelope flag alone: 204/empty-body
      // responses are legitimate successes with undefined data.
      if (response.success) {
        return response.data as T;
      }
      throw new TranslatableError(
        response.error?.userMessage || 'Request failed',
        response.error?.userMessageKey || 'api.errors.unknownError.message',
        response.error?.serverDetail
      );
    },
    []
  );

  const get = useCallback(
    async <T>(endpoint: string, options?: GetCallOptions) => {
      return unwrap<T>(
        await unifiedApiClient.get<T>(endpoint, {
          enableGlobalErrorHandling:
            options?.enableGlobalErrorHandling ?? enableGlobalErrorHandling,
          headers: options?.headers,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const post = useCallback(
    async <T>(
      endpoint: string,
      data?: unknown,
      headers?: Record<string, string>
    ) => {
      return unwrap<T>(
        await unifiedApiClient.post<T>(endpoint, data, {
          enableGlobalErrorHandling,
          headers,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const put = useCallback(
    async <T>(
      endpoint: string,
      data?: unknown,
      headers?: Record<string, string>
    ) => {
      return unwrap<T>(
        await unifiedApiClient.put<T>(endpoint, data, {
          enableGlobalErrorHandling,
          headers,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const patch = useCallback(
    async <T>(
      endpoint: string,
      data?: unknown,
      headers?: Record<string, string>
    ) => {
      return unwrap<T>(
        await unifiedApiClient.patch<T>(endpoint, data, {
          enableGlobalErrorHandling,
          headers,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const remove = useCallback(
    async <T>(endpoint: string, headers?: Record<string, string>) => {
      return unwrap<T>(
        await unifiedApiClient.delete<T>(endpoint, {
          enableGlobalErrorHandling,
          headers,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const upload = useCallback(
    async <T>(
      endpoint: string,
      file: File,
      additionalData?: Record<string, unknown>,
      options?: UploadCallOptions
    ) => {
      return unwrap<T>(
        await unifiedApiClient.uploadFile<T>(endpoint, file, {
          enableGlobalErrorHandling,
          metadata: additionalData,
          ...options,
        })
      );
    },
    [enableGlobalErrorHandling, unwrap]
  );

  const toTranslatableError = useCallback((error: unknown): never => {
    if (error instanceof UnifiedApiError) {
      throw new TranslatableError(
        error.details.userMessage || 'Request failed',
        error.details.userMessageKey || 'api.errors.unknownError.message'
      );
    }
    throw error;
  }, []);

  const download = useCallback(
    async (
      endpoint: string,
      filename?: string,
      options?: DownloadCallOptions
    ): Promise<Blob> => {
      try {
        return await unifiedApiClient.downloadFile(endpoint, filename, {
          enableGlobalErrorHandling,
          ...options,
        });
      } catch (error) {
        return toTranslatableError(error);
      }
    },
    [enableGlobalErrorHandling, toTranslatableError]
  );

  const openInNewTab = useCallback(
    async (endpoint: string, options?: DownloadCallOptions): Promise<void> => {
      try {
        await unifiedApiClient.openFileInNewTab(endpoint, {
          enableGlobalErrorHandling,
          ...options,
        });
      } catch (error) {
        toTranslatableError(error);
      }
    },
    [enableGlobalErrorHandling, toTranslatableError]
  );

  const cancelAllRequests = useCallback(
    () => unifiedApiClient.cancelAllRequests(),
    []
  );

  return useMemo(
    () => ({
      get,
      post,
      put,
      patch,
      delete: remove,
      upload,
      download,
      openInNewTab,
      cancelAllRequests,
    }),
    [
      get,
      post,
      put,
      patch,
      remove,
      upload,
      download,
      openInNewTab,
      cancelAllRequests,
    ]
  );
}
